/**
 * Datums-Helfer für die Kalender-Ansichten. Anzeige in lokaler Zeit (de-DE);
 * Server liefert/erwartet ISO-UTC.
 */
export const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const

export function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
export function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}
export function addMonths(d: Date, n: number): Date {
  const x = new Date(d)
  x.setMonth(x.getMonth() + n)
  return x
}
/** Montag als Wochenanfang. */
export function startOfWeek(d: Date): Date {
  const x = startOfDay(d)
  const wd = (x.getDay() + 6) % 7 // Mo=0
  return addDays(x, -wd)
}
export function startOfMonth(d: Date): Date {
  const x = startOfDay(d)
  x.setDate(1)
  return x
}
export function endOfMonth(d: Date): Date {
  const x = startOfMonth(d)
  x.setMonth(x.getMonth() + 1)
  return addDays(x, -1)
}
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  )
}
export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

/** 6×7-Matrix (Mo–So) für die Monatsansicht. */
export function monthMatrix(cursor: Date): Date[][] {
  const first = startOfWeek(startOfMonth(cursor))
  const weeks: Date[][] = []
  for (let w = 0; w < 6; w++) {
    const row: Date[] = []
    for (let d = 0; d < 7; d++) row.push(addDays(first, w * 7 + d))
    weeks.push(row)
  }
  return weeks
}

export function weekDays(cursor: Date): Date[] {
  const first = startOfWeek(cursor)
  return Array.from({ length: 7 }, (_, i) => addDays(first, i))
}

const fmtTime = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })
const fmtMonthYear = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' })
const fmtDayLong = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })
const fmtRange = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' })

export const formatTime = (iso: string): string => fmtTime.format(new Date(iso))
export const formatMonthYear = (d: Date): string => fmtMonthYear.format(d)
export const formatDayLong = (d: Date): string => fmtDayLong.format(d)
export const formatRange = (d: Date): string => fmtRange.format(d)

/** Für <input type="datetime-local"> (lokale Zeit ohne Zeitzone). */
export function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
export function fromLocalInput(v: string): Date {
  return new Date(v)
}

/** Kompakte Relativzeit auf Deutsch, z. B. „gerade eben", „vor 5 Min", „vor 2 Std". */
export function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'gerade eben'
  if (min < 60) return `vor ${min} Min`
  const h = Math.floor(min / 60)
  if (h < 24) return `vor ${h} Std`
  const d = Math.floor(h / 24)
  if (d < 7) return `vor ${d} ${d === 1 ? 'Tag' : 'Tagen'}`
  return new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' }).format(new Date(iso))
}
